import type { Item, Field } from "./infra";

export function dnsConnectionAddresses(text: string): string[] {
  return [...new Set(text.split(/\r?\n/).map(value => value.trim().toLowerCase().replace(/\.$/, "")).filter(Boolean))];
}

export function dnsConnectionType(address: string): "A" | "AAAA" | "CNAME" {
  return address.includes(":") ? "AAAA" : /^\d{1,3}(\.\d{1,3}){3}$/.test(address) ? "A" : "CNAME";
}

export function dnsRecordName(zone: string, value: unknown): string {
  const name = String(value || "").trim().toLowerCase().replace(/\.$/, "");
  if (name === "@") return zone;
  return name && !name.includes(".") ? name + "." + zone : name;
}
export function dnsRecordFields(zones: Item[], form: Item, editing: boolean): Field[] {
  const zone = zones.find(z => z["id"] === form["zoneId"])?.["name"] || "example.com";
  const type = form["type"] || "A";
  const examples: Item = { A: "192.0.2.10", AAAA: "2001:db8::10", CNAME: "app.example.net", TXT: "v=spf1 include:example.net ~all", MX: "mail.example.com", CAA: '0 issue "letsencrypt.org"', SRV: "10 5 5060 sip.example.com" };
  const contentLabels: Item = { A: "대상 IPv4 주소", AAAA: "대상 IPv6 주소", CNAME: "대상 도메인", MX: "메일 서버 도메인", TXT: "텍스트 값", CAA: "인증기관 정책", SRV: "우선순위 · 가중치 · 포트 · 대상" };
  return [
    { key: "zoneId", label: "연결할 도메인 영역", type: "select", required: true, disabled: editing, placeholder: "예: example.com",
      options: zones.map(z => ({value:z["id"],label:z["name"]})), hint: editing ? "기존 레코드가 속한 영역입니다. 다른 영역에는 새 레코드를 추가하세요." : "이 영역의 Cloudflare API 토큰으로 저장합니다." },
    { key: "type", label: "레코드 유형", type: "select", required: true, placeholder: "예: A (IPv4)",
      options: Object.entries(contentLabels).map(([value,label]) => ({value,label:value+" · "+label})) },
    { key: "name", label: "레코드 이름", required: true, placeholder: type === "SRV" ? "_sip._tcp."+zone : "app."+zone,
      hint: "@ = "+zone+", app = app."+zone+". 전체 도메인도 입력할 수 있습니다."+ (form["name"] ? " 저장할 이름: "+dnsRecordName(zone,form["name"]) : "") },
    { key: "content", label: contentLabels[type], required: true, placeholder: "예: "+examples[type],
      hint: type === "SRV" ? "예: 10 5 5060 sip.example.com — 우선순위가 낮은 서버가 먼저 선택되며 같은 우선순위에서는 가중치로 분배합니다." : type === "CNAME" || type === "MX" ? "IP나 URL 대신 대상 서버의 전체 도메인을 입력하세요." : "" },
    { key: "ttl", label: "TTL (초)", type: "number", required: true, placeholder: "예: 300 (5분), 1 (자동)", hint: "DNS 캐시 유지 시간: 자동 1 또는 60~86400초. " },
    ...(type === "MX" ? [{key:"priority",label:"메일 서버 우선순위",type:"number",required:true,placeholder:"예: 10",hint:"0~65535. 숫자가 낮을수록 먼저 사용합니다. 예: 주 서버 10, 예비 서버 20."}] : []),
    { key: "comment", label: "설명 (선택)", placeholder: "예: 운영 웹서버 연결" },
    ...(editing && !form["managed"] ? [{key:"confirmExternal",label:"외부 레코드의 현재 값과 수정 영향을 확인했습니다",type:"checkbox",hint:"저장 시 Cloudflare의 실제 레코드가 수정됩니다."}] : []),
  ];
}
export function dnsRecordPayload(form: Item, zone: string): Item {
  return {
    type: form["type"], name: dnsRecordName(zone, form["name"]), content: String(form["content"] || "").trim(),
    ttl: Number(form["ttl"]), proxied: false,
    priority: form["type"] === "MX" && form["priority"] !== "" && form["priority"] != null ? Number(form["priority"]) : null,
    comment: form["comment"] || "", confirmExternal: !!form["confirmExternal"], serviceId: form["serviceId"] || null,
  };
}

const syncDateFormatter = new Intl.DateTimeFormat("ko-KR", {
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "Asia/Seoul",
});

export function dnsSyncTime(value: unknown, now = Date.now()): { datetime: string; absolute: string; relative: string } | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const timestamp = new Date(value);
  if (!Number.isFinite(timestamp.getTime())) return null;
  const minutes = Math.floor(Math.max(0, now - timestamp.getTime()) / 60000);
  const relative = minutes < 1 ? "방금 전"
    : minutes < 60 ? minutes + "분 전"
    : minutes < 1440 ? Math.floor(minutes / 60) + "시간 전"
    : Math.floor(minutes / 1440) + "일 전";
  return { datetime: timestamp.toISOString(), absolute: syncDateFormatter.format(timestamp), relative };
}
